import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Zap, X, ArrowRight } from 'lucide-react';
import {
  CONDITIONAL_CARD_TYPES,
  outcomeOptionsForSource,
  sourceCardTypeLabel,
  buildSourceLabel,
} from './conditionalInject';

// Two-decision picker: WHEN (trigger) + THEN (card type) for a conditional follow-up.
// onConfirm receives { conditional, targetType, sourceRoles, sourceNote }.
const ConditionalInjectPicker = ({ sourceItem, sourceTaskIndex = null, onCancel, onConfirm }) => {
  // Recommended "within" minutes = the source's SLA (the task's SLA for a task trigger,
  // otherwise the card's total SLA = sum of task SLAs, or the card's own SLA).
  const tasks = sourceItem?.meta?.tasks || [];
  const taskSla = sourceTaskIndex != null ? (parseInt(tasks?.[sourceTaskIndex]?.sla) || 0) : 0;
  const cardSla = tasks.length
    ? tasks.reduce((s, t) => s + (parseInt(t.sla) || 0), 0)
    : (parseInt(sourceItem?.meta?.sla?.withinMinutes) || 0);
  const sourceSla = sourceTaskIndex != null ? (taskSla || cardSla) : cardSla;

  const [triggerType, setTriggerType] = useState('timeout');
  const outcomeOptions = outcomeOptionsForSource(sourceItem?.group);
  const [outcome, setOutcome] = useState(outcomeOptions[0] || '');
  const [targetType, setTargetType] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const needsMinutes = triggerType === 'timeout' || triggerType === 'completed';
  const canContinue =
    !!targetType &&
    (needsMinutes ? sourceSla > 0 : !!outcome);

  const handleContinue = () => {
    if (!canContinue) return;
    const conditional = {
      sourceItemId: sourceItem.id,
      sourceTaskIndex: sourceTaskIndex,
      sourceGroup: sourceItem.group,
      sourceLabel: buildSourceLabel(sourceItem, sourceTaskIndex),
      triggerType,
      withinMinutes: needsMinutes ? sourceSla : null,
      inheritsSourceSla: needsMinutes,
      outcome: triggerType === 'outcome' ? outcome : null,
    };
    onConfirm({
      conditional,
      targetType,
      sourceRoles: sourceItem?.meta?.scenarioRoles || [],
      sourceNote: sourceItem?.meta?.assignmentNote || '',
    });
  };

  const TriggerRow = ({ value, children }) => (
    <label
      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
        triggerType === value
          ? 'border-amber-400 bg-amber-50'
          : 'border-gray-200 hover:border-amber-200'
      }`}
      data-testid={`conditional-trigger-${value}`}
    >
      <input
        type="radio"
        name="conditional-trigger"
        checked={triggerType === value}
        onChange={() => setTriggerType(value)}
        className="h-4 w-4 text-amber-600 focus:ring-amber-500"
      />
      <div className="flex-1 flex items-center gap-2 text-sm text-gray-800">{children}</div>
    </label>
  );

  return createPortal((
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4" data-testid="conditional-inject-picker" onClick={onCancel}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4">
          <div className="flex items-center gap-2 text-white">
            <Zap className="h-5 w-5" />
            <div>
              <h3 className="text-base font-bold leading-tight">Conditional follow-up</h3>
              <p className="text-[12px] text-amber-50/90">
                On {sourceCardTypeLabel(sourceItem)}
                {sourceTaskIndex != null ? ` · Task ${sourceTaskIndex + 1}` : ''}
                {sourceTaskIndex != null && taskSla > 0 && (
                  <span className="ml-1 font-bold" data-testid="picker-task-sla"> · SLA {taskSla}m</span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="text-white/90 hover:text-white" data-testid="conditional-picker-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* WHEN */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">When</p>
            <div className="space-y-2">              <TriggerRow value="timeout">
                <span>If <b>not</b> completed within</span>
                {triggerType === 'timeout' && <b>{sourceSla || '—'} min</b>}
              </TriggerRow>

              <TriggerRow value="completed">
                <span>If <b>completed</b> within</span>
                {triggerType === 'completed' && <b>{sourceSla || '—'} min</b>}
              </TriggerRow>

              {outcomeOptions.length > 0 && (
                <TriggerRow value="outcome">
                  <span>If outcome is</span>
                  {triggerType === 'outcome' && (
                    <select
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                      className="h-7 rounded border border-gray-300 px-2 text-sm bg-white"
                      data-testid="conditional-outcome-select"
                    >
                      {outcomeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                </TriggerRow>
              )}
            </div>
            {(triggerType === 'timeout' || triggerType === 'completed') && (
              <p className="mt-2 text-[11px] text-amber-600" data-testid="picker-recommended-hint">
                Linked to {sourceTaskIndex != null ? "this task's SLA" : "the source card SLA"}. Changing that SLA updates this trigger automatically.
              </p>
            )}
            {needsMinutes && sourceSla <= 0 && (
              <p className="mt-2 text-[11px] font-semibold text-red-600">Set an SLA on the source before adding this follow-up.</p>
            )}
          </div>

          {/* THEN */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Then inject</p>
            <div className="grid grid-cols-2 gap-2">
              {CONDITIONAL_CARD_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTargetType(t.key)}
                  data-testid={`conditional-type-${t.key}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${
                    targetType === t.key
                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                      : 'border-gray-200 text-gray-700 hover:border-amber-200'
                  }`}
                >
                  <span className="text-lg">{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!canContinue}
            data-testid="conditional-picker-continue"
            className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default ConditionalInjectPicker;
